// RedirectIfAuthenticated
//
// Wrap auth-acquisition pages (e.g. `/login`, `/register/client`) so that an
// already-signed-in user gets bounced to their role-specific dashboard
// instead of being asked to sign in again.
//
// Why a wrapper rather than inline `useEffect` redirects on each page?
//   * Single source of truth for the policy (next time we add a route like
//     /forgot-password, we just wrap it once).
//   * Renders nothing until `isCheckingAuth` settles — avoids the half-second
//     flash of the login form for refreshed sessions.
//   * Plays nicely with the cached-profile fast-boot path in AuthContext.
//
// Routes that intentionally remain reachable while authenticated:
//   * /pricing            — logged-in users buy upgrades / AI credits here
//   * /invite/:token      — accepting an invite may switch accounts/roles
//   * /trial-expired      — landing for an expired trial owner
//   * /terms, /privacy    — content pages
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getDashboardPathForUser } from '../../lib/auth-routing';

export default function RedirectIfAuthenticated({ children }) {
  const { isAuthenticated, isCheckingAuth, user } = useAuth();
  const location = useLocation();

  // Don't flash the login form while the cookie session is being validated.
  if (isCheckingAuth) return null;

  if (isAuthenticated && user) {
    const target = getDashboardPathForUser(user);
    return <Navigate to={target} replace state={{ from: location }} />;
  }

  return children;
}
